import React, { useEffect, useMemo, useState } from 'react';
import InlineLoading from './InlineLoading';

const PosSessionModal = ({
  isOpen,
  mode = 'open',
  registers = [],
  registersByBranch = {},
  categoriesByBranch = {},
  branches = [],
  activeBranchId,
  loading = false,
  error,
  activeRegisterId,
  expectedCash,
  expectedCashLoading = false,
  expectedCashError,
  openSales = [],
  openSalesLoading = false,
  openSalesError,
  onOpenSale,
  onCancelSale,
  onSelectSession,
  onOpenSession,
  onCloseSession,
  onClose,
  currentUserId,
  canOpenSession = true,
  canCloseSession = true,
  canOpenSale = true,
  canCancelSale = true,
  canManageRegisters = false,
  onGoToRegisterSettings,
  onGoHome,
}) => {
  const [selectedId, setSelectedId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

  useEffect(() => {
    if (!isOpen) return;
    const nextBranchId =
      activeBranchId
      || branches[0]?.id
      || Object.keys(registersByBranch || {})[0]
      || '';
    setSelectedBranchId(nextBranchId ? String(nextBranchId) : '');
    setOpeningCash('');
    setClosingCash('');
    setCloseReason('');
  }, [isOpen, activeBranchId, branches, registersByBranch]);

  const branchRegisters = useMemo(() => {
    if (selectedBranchId && registersByBranch) {
      const direct = registersByBranch[selectedBranchId];
      if (direct) return direct;
      const numericKey = Number(selectedBranchId);
      if (Number.isInteger(numericKey) && registersByBranch[numericKey]) {
        return registersByBranch[numericKey];
      }
    }
    return registers;
  }, [registers, registersByBranch, selectedBranchId]);

  useEffect(() => {
    if (!isOpen) return;
    const hasActiveInBranch = branchRegisters.some(
      (register) => String(register.id) === String(activeRegisterId)
    );
    const nextId = hasActiveInBranch
      ? activeRegisterId
      : branchRegisters[0]?.id || registers[0]?.id || '';
    setSelectedId(nextId ? String(nextId) : '');
  }, [isOpen, activeRegisterId, branchRegisters, registers]);

  const selectedRegister = useMemo(
    () => branchRegisters.find((register) => String(register.id) === String(selectedId)),
    [branchRegisters, selectedId]
  );
  const activeSession = selectedRegister?.activeSession || null;
  const sessionOwnerId = activeSession?.openedBy;
  const isSessionOwner = activeSession && currentUserId
    ? String(sessionOwnerId) === String(currentUserId)
    : false;
  const sessionOwnerLabel = activeSession?.OpenedByUser?.name
    || activeSession?.OpenedByUser?.username
    || (sessionOwnerId ? `Usuario #${sessionOwnerId}` : 'otro usuario');
  const isSessionLocked = Boolean(activeSession) && !isSessionOwner;
  const parsedClosingCash = closingCash === '' ? Number.NaN : Number(closingCash);
  const closingCashValue = Number.isFinite(parsedClosingCash) ? parsedClosingCash : null;
  const expectedCashValue = Number.isFinite(Number(expectedCash)) ? Number(expectedCash) : null;
  const cashDifference = closingCashValue !== null && expectedCashValue !== null
    ? closingCashValue - expectedCashValue
    : null;
  const requiresReason = cashDifference !== null && cashDifference !== 0;
  const missingReason = requiresReason && !closeReason.trim();
  const hasOpenSales = Array.isArray(openSales) && openSales.length > 0;
  const differenceClassName = cashDifference === null
    ? 'text-gray-700'
    : cashDifference === 0
      ? 'text-green-600'
      : 'text-red-600';

  if (!isOpen) return null;

  const showBranchSidebar = Array.isArray(branches) && branches.length > 1;
  const resolvedBranchId = selectedBranchId || String(activeBranchId || '');
  const branchCategories = (() => {
    if (!resolvedBranchId || !categoriesByBranch) return [];
    const direct = categoriesByBranch[resolvedBranchId];
    if (direct) return direct;
    const numericKey = Number(resolvedBranchId);
    if (Number.isInteger(numericKey) && categoriesByBranch[numericKey]) {
      return categoriesByBranch[numericKey];
    }
    return [];
  })();

  const resolveRegisterCategories = (register) => {
    if (!register || !resolvedBranchId) return [];
    return (branchCategories || []).filter((category) => {
      if (category?.isActive === false) return false;
      const rows =
        category?.CategoryAvailabilities
        || category?.CategoryAvailability
        || [];
      return rows.some((row) => {
        if (row?.isActive === false) return false;
        if (String(row.branchId) !== String(resolvedBranchId)) return false;
        if (!row.cashRegisterId) return true;
        return String(row.cashRegisterId) === String(register.id);
      });
    });
  };

  const renderRegisterStatus = (register) => {
    if (!register?.activeSession) {
      return { label: 'Disponible', className: 'text-green-700 bg-green-50 border-green-200' };
    }
    const openedBy = String(register.activeSession.openedBy || '');
    if (openedBy && String(openedBy) === String(currentUserId)) {
      return { label: 'Abierta por ti', className: 'text-blue-700 bg-blue-50 border-blue-200' };
    }
    return { label: 'Ocupada', className: 'text-red-700 bg-red-50 border-red-200' };
  };

  const renderOpen = () => (
    <>
      <h2 className="text-lg font-semibold mb-3">Abrir caja</h2>
      <div className={`flex gap-4 ${showBranchSidebar ? '' : 'flex-col'}`}>
        {showBranchSidebar && (
          <aside className="w-52 border-r border-gray-200 pr-3">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Sucursales</div>
            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1">
              {branches.map((branch) => {
                const isActive = String(branch.id) === String(resolvedBranchId);
                const branchRegistersCount =
                  registersByBranch?.[branch.id]?.length
                  ?? registersByBranch?.[String(branch.id)]?.length
                  ?? 0;
                return (
                  <button
                    key={branch.id}
                    type="button"
                    className={`px-3 py-2 rounded-lg border text-left ${
                      isActive
                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-transparent hover:border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedBranchId(String(branch.id))}
                  >
                    <div className="text-sm font-semibold">{branch.branchName || `Sucursal ${branch.id}`}</div>
                    <div className="text-xs text-gray-500">{branchRegistersCount} cajas</div>
                  </button>
                );
              })}
            </div>
          </aside>
        )}
        <div className="flex-1 min-w-0">
          {loading && (
            <div className="text-sm text-gray-600 mb-4">
              <InlineLoading label="Cargando cajas" />
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-600 mb-4">{error}</div>
          )}
          {!loading && !error && branchRegisters.length === 0 ? (
            <>
              <p className="text-sm text-gray-600">
                {canManageRegisters
                  ? 'No hay cajas creadas en esta sucursal. Configura una para poder abrir punto de venta.'
                  : 'No hay cajas creadas en esta sucursal. Solicita al encargado que agregue una caja.'}
              </p>
              <div className="flex justify-end gap-2 mt-4">
                {canManageRegisters ? (
                  <button
                    type="button"
                    className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={onGoToRegisterSettings}
                  >
                    Ir a configuración de cajas
                  </button>
                ) : (
                  <button
                    type="button"
                    className="px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                    onClick={onGoHome}
                  >
                    Ir al inicio
                  </button>
                )}
              </div>
            </>
          ) : (
            !loading && !error && (
              <div className="space-y-3">
                {branchRegisters.map((register) => {
                  const isSelected = String(register.id) === String(selectedId);
                  const status = renderRegisterStatus(register);
                  const categories = resolveRegisterCategories(register);
                  const maxVisible = 6;
                  const visibleCategories = categories.slice(0, maxVisible);
                  const remaining = categories.length - visibleCategories.length;
                  return (
                    <button
                      key={register.id}
                      type="button"
                      onClick={() => setSelectedId(String(register.id))}
                      className={`w-full text-left border rounded-xl p-4 transition ${
                        isSelected
                          ? 'border-blue-400 ring-1 ring-blue-200 bg-blue-50/40'
                          : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-800">
                            {register.name} {register.code ? `(${register.code})` : ''}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {register.maxOpenSales != null ? `Limite ventas abiertas: ${register.maxOpenSales}` : 'Sin limite de ventas abiertas'}
                          </div>
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full border ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          Categorias habilitadas
                        </div>
                        {categories.length === 0 ? (
                          <div className="text-xs text-gray-500">
                            Sin categorias asignadas.
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {visibleCategories.map((category) => (
                              <span
                                key={category.id}
                                className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700"
                              >
                                {category.name}
                              </span>
                            ))}
                            {remaining > 0 && (
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                +{remaining} mas
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {selectedRegister && (
            <div className="mt-4">
              {activeSession ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 mb-4">
                  {isSessionLocked ? (
                    <>
                      Caja abierta por {sessionOwnerLabel}. Espera a que se cierre para usarla.
                    </>
                  ) : (
                    <>
                      Caja abierta desde {new Date(activeSession.openedAt).toLocaleTimeString()}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fondo inicial
                  </label>
                  <input
                    className="border border-gray-300 rounded p-2 w-full mb-4"
                    type="number"
                    min="0"
                    value={openingCash}
                    onChange={(e) => setOpeningCash(e.target.value)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {branchRegisters.length > 0 && (
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            className="px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            onClick={onClose}
          >
            Cancelar
          </button>
          {activeSession ? (
            !isSessionLocked && (
              <button
                type="button"
                className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => onSelectSession?.(selectedRegister, activeSession, resolvedBranchId)}
              >
                Usar sesion abierta
              </button>
            )
          ) : (
            canOpenSession && (
              <button
                type="button"
                className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => onOpenSession?.(selectedRegister, openingCash, resolvedBranchId)}
                disabled={!selectedRegister}
              >
                Abrir caja
              </button>
            )
          )}
        </div>
      )}
    </>
  );

  const renderClose = () => (
    <>
      <h2 className="text-lg font-semibold mb-3">Cerrar caja</h2>
      <div className="text-sm text-gray-700 mb-4">
        {selectedRegister?.name || 'Caja'} {selectedRegister?.code ? `(${selectedRegister.code})` : ''}
      </div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Efectivo contado
      </label>
      <input
        className="border border-gray-300 rounded p-2 w-full mb-4"
        type="number"
        min="0"
        value={closingCash}
        onChange={(e) => setClosingCash(e.target.value)}
      />
      <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 mb-4">
        <div className="flex items-center justify-between">
          <span>Efectivo esperado</span>
          <span>
            {expectedCashLoading
              ? <InlineLoading label="Calculando" />
              : expectedCashValue === null
                ? '-'
                : formatCurrency(expectedCashValue)}
          </span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span>Diferencia (contado - esperado)</span>
          <span className={differenceClassName}>
            {cashDifference === null ? '-' : formatCurrency(cashDifference)}
          </span>
        </div>
        {expectedCashError && (
          <div className="text-xs text-red-600 mt-2">{expectedCashError}</div>
        )}
      </div>
      <div
        className={`border rounded p-3 text-sm mb-4 ${
          hasOpenSales ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="font-medium text-gray-800 mb-2">Ventas abiertas en esta caja</div>
        {openSalesLoading ? (
          <div className="text-gray-600">
            <InlineLoading label="Cargando ventas abiertas" />
          </div>
        ) : openSalesError ? (
          <div className="text-red-600">{openSalesError}</div>
        ) : hasOpenSales ? (
          <>
            <div className="text-xs text-red-700 mb-2">
              No puedes cerrar la caja mientras existan ventas abiertas.
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {openSales.map((sale) => {
                const clientName = sale?.Client?.name || 'Venta Detalle';
                const totalLabel = Number.isFinite(Number(sale?.totalAmount))
                  ? formatCurrency(sale.totalAmount)
                  : '';
                return (
                  <div
                    key={sale.id}
                    className="flex items-start justify-between gap-2 border border-red-100 bg-white rounded p-2"
                  >
                    <div>
                      <div className="font-medium text-gray-800">Venta #{sale.id}</div>
                      <div className="text-xs text-gray-600">
                        {clientName}{totalLabel ? ` · ${totalLabel}` : ''}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {canOpenSale && (
                        <button
                          type="button"
                          className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                          onClick={() => onOpenSale?.(sale)}
                        >
                          Continuar
                        </button>
                      )}
                      {canCancelSale && (
                        <button
                          type="button"
                          className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                          onClick={() => onCancelSale?.(sale)}
                        >
                          Anular
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-gray-600">No hay ventas abiertas.</div>
        )}
      </div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Motivo de diferencia {requiresReason ? '(obligatorio)' : '(si aplica)'}
      </label>
      <textarea
        className="border border-gray-300 rounded p-2 w-full mb-4"
        rows="3"
        value={closeReason}
        onChange={(e) => setCloseReason(e.target.value)}
        required={requiresReason}
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
          onClick={onClose}
        >
          Cancelar
        </button>
        {canCloseSession && (
          <button
            type="button"
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => onCloseSession?.(closingCash, closeReason)}
            disabled={missingReason || hasOpenSales}
          >
            Cerrar caja
          </button>
        )}
      </div>
    </>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-lg shadow-lg p-6 w-full ${
          mode === 'open' ? 'max-w-5xl' : 'max-w-md'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {mode === 'close' ? renderClose() : renderOpen()}
      </div>
    </div>
  );
};

export default PosSessionModal;
